import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CaretUpDownIcon,
  CheckIcon,
  XIcon,
  PlusIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { api, ApiError } from '~/lib/api-client';
import { useConfirm } from '~/components/confirm-dialog';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Input } from '~/components/ui/input';
import { isValidEntry } from './share-people-input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { Checkbox } from '~/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { ActionBar } from '~/components/ui/action-bar';
import { cn } from '~/lib/utils';
import { AppSharePopover, appRowAudience } from './app-share-control';
import { APP_AUDIENCE_COPY } from '~/features/coding/coding-events';
import { FilesEditor } from './files-editor';
import { BrandKitControl, inheritedAccent, useAppBrandKit } from './app-brand-kit-control';
import { EditorsSection, showsEditorsSection } from './editors-section';
import { APP_DRAFTS_FLAG, useFeatureFlag } from '~/hooks/queries/use-feature-flags';
import { BRAND_KIT_FLAG } from '~/lib/feature-flag-names';
import { resolveApplyScopesAction } from './app-token-scopes';
import { formatDateTime } from '~/lib/local-datetime';
import { budgetRefused } from '~/features/limits/budget-refusal';
import type { AppStorage } from '~/hooks/queries/use-app-storage';

export type AppStatus = 'draft' | 'live' | 'archived';
export type AppTheme = 'light' | 'dark' | 'system';
export type AppRobots = 'index' | 'noindex';
export type AppVisibility = 'public' | 'private';
export type AppDomainStatus = 'pending' | 'verifying' | 'verified' | 'failed';

export interface AdminApp {
  /** FRONT-1602: 'app' | 'deck'. Decks are listed on the Knowledge page's Slides tab, not on Apps. */
  kind?: string;
  slug: string;
  title: string;
  description: string | null;
  emoji: string | null;
  status: AppStatus;
  /** FRONT-3021: set when the app was published from Pro Builder; null/absent otherwise. */
  producer?: 'pro_builder' | null;
  hasContent: boolean;
  faviconUrl: string | null;
  logoUrl: string | null;
  accentColor: string | null;
  theme: AppTheme;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImageUrl: string | null;
  robots: AppRobots;
  visibility: AppVisibility;
  customDomain: string | null;
  domainStatus: AppDomainStatus | null;
  domainVerificationToken: string | null;
  domainLastCheckedAt: string | null;
  domainVerifiedAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  shareAllowedEmails: string[] | null;
  /** FRONT-1926: the colleagues remembered from a share "Only you" paused
   *  rather than deleted — owner-only, `[]`/`null` when there is none. Lets
   *  the Share control offer "Share again with the same N colleagues?"
   *  instead of silently restoring them. */
  pausedShareEmails?: string[] | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdBy: string | null;
  // FRONT-181: internal ownership + workspace sharing (distinct from the
  // external web `visibility` above). `isOwner` gates edit affordances.
  ownerUserId: string | null;
  workspaceShared: boolean;
  isOwner: boolean;
  /** FRONT-2937: one of the app's editors, who opens it before it is live and changes only its code. */
  isEditor?: boolean;
  /** Server-confirmed code-editor access to this app through Pro Builder Git. */
  proBuilderCodeEditable?: boolean;
  /** False for an editor the owner kept to "Code only"; sent only while `eddy.app-drafts` is on. */
  seesData?: boolean;
  /** FRONT-2510: the owner's display name — set only on a colleague's app whose owner is known. */
  ownerName?: string | null;
  /** FRONT-1880: can ANY colleague open it — the whole workspace, a named
   *  few, or a legacy ownerless row. Server-computed from the full row,
   *  because the named list is owner-only in this payload: deriving it here
   *  from `shareAllowedEmails` badged the colleague an app was shared with
   *  "Only you" on their own list. The audience badge reads THIS. */
  colleaguesCanOpen: boolean;
  /** FRONT-1880: why this viewer cannot change who opens the app, in the
   *  words to show them — null when they can. One rule (`audienceLockReason`,
   *  `src/apps/audience.ts`) shared with the route that refuses, so the
   *  greyed-out reason and the server's answer are the same sentence.
   *  Optional so a cached payload from before this field reads as no lock. */
  audienceLock?: string | null;
  /** FRONT-1880: which shape "Shared" currently has — `people` when the app
   *  is shared with a named few, `workspace` otherwise. Server-derived
   *  (`shareScopeFrom`) so the control opens on the state it will edit.
   *  Owner-only on the wire like `shareAllowedEmails` below — `apps-routes.ts`
   *  sends `null` (not merely omits the key) to every other viewer, so `null`
   *  is a real, common case here, not just a stale-payload fallback (PR
   *  review finding, DerStoffel, 2026-09-16 — this type previously typed
   *  the field `undefined`-only, which doesn't match what a non-owner
   *  viewer actually receives). */
  shareScope?: 'workspace' | 'people' | null;
  /** FRONT-2566: do colleagues find this Public app in their lists? Owner-only
   *  like `shareScope`: null for everyone else, meaning unknown. */
  listedToColleagues?: boolean | null;
  /** FRONT-622 / FRONT-2938: the viewer's own draft of the app (its Building
   *  Session) — powers the "Continue Coding" jump. Only the list and the app
   *  read send it; null when the viewer has none, never someone else's. */
  codingSessionId?: string | null;
  /** FRONT-1924: THIS viewer's own pin, read from `user_settings` — never a
   *  property of the app itself, so a colleague's favourite never shows up
   *  here. Only the list endpoint (`GET /api/admin/apps`) sets this;
   *  optional so a row echoed back by a different mutation (share, audience,
   *  …) reads as "unknown" rather than falsely `false`. */
  isFavourite?: boolean;
  /** FRONT-2934: when a restore lost part of this app's history. Owner-only; null for everyone else. */
  repoGapAt?: string | null;
  /** FRONT-3007: data-storage figures, on the list rows of apps whose data
   *  this viewer may use (the owner); null for everyone else. */
  storage?: AppStorage | null;
  /** FRONT-3158 / FRONT-3369: how many changes the live app doesn't serve yet. Owner-only, from the app read and the list; null when unknown. */
  unpublishedChanges?: number | null;
  /** FRONT-3369: drafts sent to the owner for review and still waiting. Owner-only while drafts are on; null otherwise. */
  draftsToReview?: number | null;
  /** FRONT-3368: whether this viewer reads the app's Activity, by the same rule its routes answer with. */
  seesActivity?: boolean;
}

/**
 * FRONT-2934: tells the owner that a restore lost part of the app's change
 * history, until they dismiss it. The files themselves were kept.
 */
/**
 * FRONT-1640 / FRONT-2937: who may open an app that is built but not published —
 * its owner (an app with a recorded owner: `isOwner` alone is also true for a
 * legacy ownerless row) and its editors. Mirrors `resolveServableApp`.
 */
export function opensBeforeLive(app: Pick<AdminApp, 'isOwner' | 'ownerUserId' | 'isEditor'>): boolean {
  return (app.isOwner && app.ownerUserId != null) || app.isEditor === true;
}

export function RepoGapNotice({ app, onDismissed }: { app: Pick<AdminApp, 'slug' | 'isOwner' | 'repoGapAt'>; onDismissed: () => void }) {
  const dismiss = useMutation({
    mutationFn: () => api.post(`/api/admin/apps/${app.slug}/repo-gap/dismiss`),
    onSuccess: onDismissed,
    onError: (e: Error) => toast.error(`Could not dismiss: ${e.message}`),
  });
  if (!app.isOwner || !app.repoGapAt) return null;
  return (
    <div
      data-testid="repo-gap-notice"
      role="status"
      className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm"
    >
      <WarningCircleIcon size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-500" />
      <p className="flex-1">
        This app was restored from a backup, and EDDY found on {formatDateTime(app.repoGapAt)} that some of its earlier
        change history couldn&rsquo;t be recovered. Its files are exactly as they were live, and its history starts again
        from them.
      </p>
      <Button variant="outline" size="sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
        Dismiss
      </Button>
    </div>
  );
}

export interface DomainInstructions {
  cnameTarget: string;
  txtName: string;
  txtValue: string;
}

export type MetadataKind = 'branding' | 'seo';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDateTime(iso, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ───────────────────────────── Content tab ────────────────────────────

export function ContentTab({ slug, onSaved, readOnly = false }: { slug: string; onSaved: () => void; readOnly?: boolean }) {
  // FRONT-935: the file-tree multi-file editor replaces the single-document
  // RawContentEditor (FRONT-684).
  return <FilesEditor slug={slug} onSaved={onSaved} readOnly={readOnly} />;
}

/** FRONT-2937: an editor of the app (not its owner) sees its preview and code, and none of the owner's controls. */
export function editsOnly(app: Pick<AdminApp, 'isOwner' | 'isEditor'>): boolean {
  return !app.isOwner && app.isEditor === true;
}

/** FRONT-2938: opens the caller's own draft of the app; a spent budget refuses it with its sentence, before anything is made. */
export async function openOwnDraftRequest(slug: string, refreshBudget: () => void): Promise<{ sessionId: string }> {
  try {
    return await api.post<{ sessionId: string }>(`/api/coding/apps/${slug}/session`);
  } catch (err) {
    throw budgetRefused(err, refreshBudget) ?? err;
  }
}

/** FRONT-2937 / FRONT-2938: what an editor reads where the owner's settings, sharing and address would be. */
/** The line above a Code only editor's preview, or null for anyone who sees the app's data. */
export function noDataLine(app: Pick<AdminApp, 'isOwner' | 'isEditor' | 'seesData' | 'ownerName'>): string | null {
  if (!editsOnly(app) || app.seesData !== false) return null;
  return `${app.ownerName ?? "The app's owner"} hasn't shared this app's data with you, so it shows no data.`;
}

export function ownerOnlySentence(app: Pick<AdminApp, 'ownerName'>): string {
  const owner = app.ownerName ?? "the app's owner";
  return `Only ${owner} can change this app's settings, sharing and address. Your changes go into your own draft, and ${owner} accepts them into the app.`;
}


export function LivePreview({ html, mode }: { html: string; mode: 'desktop' | 'mobile' }) {
  // Fixed virtual viewport per mode. Desktop: 1280×800 so apps render
  // their non-mobile layout regardless of how narrow the preview pane is.
  // Mobile: 390×844 (≈ iPhone) so mobile media-query branches kick in.
  // The iframe sits at fixed virtual dimensions; CSS transform scales it
  // down to fit the visible pane. The pane size is measured via
  // ResizeObserver — important: we never compute the iframe's *own*
  // dimensions from the wrapper's height, which would create a feedback
  // loop and infinite-scroll the page.
  const dim = mode === 'mobile' ? { w: 390, h: 844 } : { w: 1280, h: 800 };
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pane, setPane] = useState({ w: 600, h: 500 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setPane({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Pick the scale that fits both dimensions, capped at 1 so we don't
  // upscale tiny previews into a blurry mess.
  const scale = Math.min(pane.w / dim.w, pane.h / dim.h, 1);
  const visualW = dim.w * scale;
  const visualH = dim.h * scale;

  // The iframe is positioned absolutely inside a "stage" sized to the
  // *visual* dimensions, not the unscaled layout box. That avoids the
  // flex-centering gotcha where the layout box (1280×800) gets centered
  // but the visible scaled content lands off-pane.
  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden bg-background ${
        mode === 'mobile' ? 'flex items-center justify-center p-4' : ''
      }`}
    >
      <div
        className="relative"
        style={{
          width: visualW,
          height: visualH,
          boxShadow: mode === 'mobile' ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
          borderRadius: mode === 'mobile' ? 16 : 0,
          overflow: 'hidden',
        }}
      >
        <iframe
          title="Live preview"
          srcDoc={html}
          sandbox="allow-scripts"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: dim.w,
            height: dim.h,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 0,
            backgroundColor: 'white',
          }}
        />
      </div>
    </div>
  );
}

// ───────────────────────────── Metadata tabs ──────────────────────────

/** The draft fields that still differ from the stored row. Pure, for the test. */
export function pendingEdits<T extends object>(draft: Partial<T>, row: T): Partial<T> {
  return Object.fromEntries(Object.entries(draft).filter(([k, v]) => row[k as keyof T] !== v)) as Partial<T>;
}

export function MetadataTab({ slug, kind, onSaved }: { slug: string; kind: MetadataKind; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-app', slug],
    queryFn: () => api.get<{ app: AdminApp }>(`/api/admin/apps/${slug}`),
  });
  const app = data?.app;

  // FRONT-1827: the owner's brand kit decides what an empty Accent falls back to. No card while the brand kit flag is off.
  const brandKitOn = useFeatureFlag(BRAND_KIT_FLAG, false);
  const ownsApp = brandKitOn && kind === 'branding' && !!app?.isOwner && app.kind !== 'deck' && app.kind !== 'sketch';
  const { data: brandKit } = useAppBrandKit(slug, ownsApp);

  const [draft, setDraft] = useState<Partial<AdminApp>>({});
  // A newer app row drops only the edits it already holds: switching the brand kit also moves
  // `updatedAt`, and must not throw away what the person typed and has not saved (FRONT-1827).
  useEffect(() => {
    if (app) setDraft((d) => pendingEdits(d, app));
  }, [app?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = <K extends keyof AdminApp>(k: K): AdminApp[K] | undefined => {
    if (k in draft) return draft[k] as AdminApp[K];
    return app?.[k];
  };
  const setVal = <K extends keyof AdminApp>(k: K, v: AdminApp[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: () => api.patch(`/api/admin/apps/${slug}`, draft),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
      onSaved();
      setDraft({});
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  if (isLoading || !app) {
    return <Skeleton className="h-60 w-full" />;
  }

  const dirtyCount = Object.keys(draft).length;
  const kitAccent = inheritedAccent(brandKit);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-4">
        {kind === 'branding' && (
          <>
            {ownsApp && <BrandKitControl slug={slug} onSaved={onSaved} />}
            <Field label="Favicon URL" hint="Square PNG/ICO. Shown in browser tabs and bookmarks. Leave empty to use the EDDY icon, unless the app sets its own.">
              <Input value={value('faviconUrl') ?? ''} onChange={(e) => setVal('faviconUrl', e.target.value || null)} placeholder="https://…/favicon.png" />
            </Field>
            <Field label="Logo URL" hint="Used by templates that render a header.">
              <Input value={value('logoUrl') ?? ''} onChange={(e) => setVal('logoUrl', e.target.value || null)} placeholder="https://…/logo.svg" />
            </Field>
            <Field
              label="Accent color"
              hint={
                kitAccent
                  ? 'Empty uses the brand kit accent. A color here wins over the kit.'
                  : 'Hex like #3b82f6. Exposed to the HTML as --app-accent.'
              }
            >
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={value('accentColor') ?? kitAccent ?? '#3b82f6'}
                  onChange={(e) => setVal('accentColor', e.target.value)}
                  className="w-16 h-9 p-1"
                />
                <Input
                  value={value('accentColor') ?? ''}
                  onChange={(e) => setVal('accentColor', e.target.value || null)}
                  placeholder={kitAccent ? `${kitAccent} (from brand kit)` : '#3b82f6'}
                />
              </div>
            </Field>
            <Field label="Theme" hint="Sets the served page's color-scheme. System follows the visitor's OS preference; Light/Dark force one mode.">
              <Combobox
                value={value('theme') ?? 'system'}
                onChange={(v) => setVal('theme', v as AppTheme)}
                placeholder="Select theme…"
                searchPlaceholder="Search theme…"
                options={[
                  { value: 'system', label: 'System' },
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ]}
              />
            </Field>
          </>
        )}

        {kind === 'seo' && (
          <>
            <Field label="Meta title" hint="Falls back to app title if empty.">
              <Input value={value('metaTitle') ?? ''} onChange={(e) => setVal('metaTitle', e.target.value || null)} maxLength={70} />
            </Field>
            <Field label="Meta description" hint="≤ 160 chars recommended. Shown in search results and social previews.">
              <Textarea value={value('metaDescription') ?? ''} onChange={(e) => setVal('metaDescription', e.target.value || null)} maxLength={300} rows={3} />
            </Field>
            <Field label="Open Graph image URL" hint="1200×630 PNG/JPG. Shown when the link is shared. Leave empty to use the auto-generated card.">
              <Input value={value('ogImageUrl') ?? ''} onChange={(e) => setVal('ogImageUrl', e.target.value || null)} placeholder="https://…/og.png" />
            </Field>
            {/* FRONT-1775 (spec Decision 6): "Search indexing" left this tab.
                Every app is served noindex now, full stop — the field only
                ever controlled whether search engines were told not to
                index a Public app, and that promise is no longer one this
                app can make selectively. */}
          </>
        )}
      </div>
      <ActionBar
        dirty={dirtyCount > 0}
        saving={save.isPending}
        onSave={() => save.mutate()}
        onDiscard={() => setDraft({})}
        message={dirtyCount === 1 ? '1 unsaved change' : `${dirtyCount} unsaved changes`}
      />
    </div>
  );
}

// ───────────────────────────── Domain tab ─────────────────────────────

export function DomainTab({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin-app', slug],
    queryFn: () => api.get<{ app: AdminApp }>(`/api/admin/apps/${slug}`),
    refetchInterval: (q) => {
      const status = q.state.data?.app?.domainStatus;
      return status === 'pending' || status === 'verifying' ? 10_000 : false;
    },
  });
  const app = data?.app;
  const [domain, setDomain] = useState('');
  const [instructions, setInstructions] = useState<DomainInstructions | null>(null);

  const setMutation = useMutation({
    mutationFn: () => api.post<{ app: AdminApp; instructions: DomainInstructions }>(`/api/admin/apps/${slug}/domain`, { domain }),
    onSuccess: (r) => {
      setInstructions(r.instructions);
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
      onSaved();
      toast.success('Domain saved — add DNS records to verify');
    },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  });

  const recheckMutation = useMutation({
    mutationFn: () => api.post(`/api/admin/apps/${slug}/domain/recheck`),
    onSuccess: () => {
      toast.info('Re-checking DNS…');
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
    },
    onError: (e: Error) => toast.error(`Recheck failed: ${e.message}`),
  });

  const clearMutation = useMutation({
    mutationFn: () => api.delete(`/api/admin/apps/${slug}/domain`),
    onSuccess: () => {
      setInstructions(null);
      setDomain('');
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
      onSaved();
      toast.success('Custom domain removed');
    },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  });

  if (!app) return <Skeleton className="h-40 w-full" />;

  const hasDomain = !!app.customDomain;
  const shownInstructions: DomainInstructions | null =
    instructions ??
    (app.customDomain && app.domainVerificationToken
      ? {
          cnameTarget: 'apps.eddy.theblockbrain.ai',
          txtName: `_eddy-verify.${app.customDomain}`,
          txtValue: app.domainVerificationToken,
        }
      : null);

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {!hasDomain && (
        <>
          <Field label="Custom domain" hint="A subdomain you control, e.g. docs.example.com. Apex domains aren't supported yet.">
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="docs.example.com" />
          </Field>
          <Button onClick={() => setMutation.mutate()} disabled={!domain || setMutation.isPending}>
            {setMutation.isPending ? 'Saving…' : 'Add domain'}
          </Button>
        </>
      )}

      {hasDomain && (
        <>
          <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-foreground">{app.customDomain}</p>
              <p className="text-xs text-muted-foreground">
                Status: <span className="font-medium">{app.domainStatus ?? '—'}</span>
                {app.domainLastCheckedAt && <> · Last check {formatDate(app.domainLastCheckedAt)}</>}
                {app.domainVerifiedAt && <> · Verified {formatDate(app.domainVerifiedAt)}</>}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => recheckMutation.mutate()} disabled={recheckMutation.isPending}>
                Recheck
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => clearMutation.mutate()} disabled={clearMutation.isPending}>
                Remove
              </Button>
            </div>
          </div>

          {shownInstructions && app.domainStatus !== 'verified' && (
            <div className="rounded-md border border-border bg-background p-3 space-y-3">
              <p className="text-sm font-medium text-foreground">Add these DNS records</p>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">1. CNAME — points your domain at EDDY</p>
                <pre className="text-xs font-mono bg-muted rounded px-2 py-1.5 overflow-x-auto">
                  {app.customDomain}  CNAME  {shownInstructions.cnameTarget}
                </pre>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">2. TXT — proves you own the domain</p>
                <pre className="text-xs font-mono bg-muted rounded px-2 py-1.5 overflow-x-auto">
                  {shownInstructions.txtName}  TXT  "{shownInstructions.txtValue}"
                </pre>
              </div>
              <p className="text-xs text-muted-foreground">
                We'll re-check every minute. Once both records resolve, the status flips to "verified" and the cert is issued automatically.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ───────────────────────────── Sharing tab ────────────────────────────

// FRONT-1880 review: `sharingChanges` lived here — the pure step that
// decided what the Sharing tab's ActionBar had to save. The tab has no save
// flow left (audience applies as it is chosen, and the named list with it),
// so the function governed no UI and its tests guarded nothing. Deleted with
// the flow rather than left as a fixture a reader would trust.

/**
 * FRONT-2078: the named colleague(s) a share is actually scoped to, or
 * `null` for a workspace-wide share (nothing more specific to name) or no
 * share at all. The one place this gets computed, so the offline dormant
 * note and the live "who can open it" hint can't independently drift into
 * disagreeing about the same fact — which is exactly what shipped first:
 * the toast for going LIVE quoted `APP_AUDIENCE_COPY.shared.hint` verbatim,
 * a fixed "People in your workspace..." string that was never scope-aware,
 * while the dormant note (below) already named the real person. Caught
 * live: an app shared with one named colleague still toasted "workspace"
 * the moment it went live.
 */
function namedShareWho(app: Pick<AdminApp, 'shareScope' | 'shareAllowedEmails'>): string | null {
  // Only email entries name anyone (FRONT-2756): a bare word saved earlier
  // opens the app to nobody, so it is never quoted as "max can open it".
  const names = (app.shareAllowedEmails ?? []).filter(isValidEntry);
  if (app.shareScope === 'people' && names.length > 0) {
    return names.length === 1 ? names[0] : `${names.length} named colleagues`;
  }
  return null;
}

/**
 * `namedShareWho`'s fallback for when there's no named list to point to —
 * split out because "no named list" means two different things and only one
 * of them is actually "everyone in your workspace".
 *
 * `shareScope`/`shareAllowedEmails` are OWNER-ONLY on the wire: `apps-routes.ts`
 * sends both `null` to every other viewer (`isOwner ? shareScopeFrom(r) : null`),
 * and `hasInternalAppAccess`'s named-people clause deliberately lets a colleague
 * named on a people-scoped share load this exact page. For that colleague,
 * `shareScope` reads `null` — not because the share is workspace-wide, but
 * because the data is redacted from them — so treating "not 'people'" as
 * "must be workspace" told a named colleague "everyone in your workspace can
 * open it" when really only a named few, quite possibly just them, can. That
 * is the exact audience-overstating confusion FRONT-2078 exists to remove,
 * now aimed at a colleague instead of the owner (PR review finding, 2026-09-16
 * — `dormant-audience.test.ts` only had owner-shaped fixtures until this fix).
 * Only a CONFIRMED `shareScope === 'workspace'` gets the specific claim;
 * anything else (including redacted-null) gets the honest vague one.
 */
function namedOrWorkspaceWho(app: Pick<AdminApp, 'shareScope' | 'shareAllowedEmails'>): string {
  return namedShareWho(app) ?? (app.shareScope === 'workspace' ? 'everyone in your workspace' : 'some of your colleagues');
}

/**
 * The precise "who can open it" sentence for a currently-shared app —
 * scope-aware, unlike the generic `APP_AUDIENCE_COPY.shared.hint` (which is
 * also used on scan surfaces, like the Apps list row, that deliberately
 * don't want per-person detail). Only meaningful when the audience actually
 * is "shared"; callers gate on that themselves.
 */
export function describeSharedHint(app: Pick<AdminApp, 'shareScope' | 'shareAllowedEmails'>): string {
  const who = namedShareWho(app);
  if (who) return `${who} can open it.`;
  return app.shareScope === 'workspace'
    ? 'People in your workspace can open it and find it in Apps.'
    : 'Some of your colleagues can open it.';
}

/**
 * FRONT-2078: whether this app's sharing decision would let someone else in
 * the moment it goes live, while it currently isn't — and if so, who.
 *
 * `colleaguesCanOpen`/`visibility` are stored independently of `status`
 * (see `appAudienceFrom` in `src/apps/audience.ts`): taking an app offline
 * never clears them, it only stops anyone from acting on them right now.
 * Before this, that meant an unpublished app always badged "Only you" with
 * nothing anywhere saying a workspace or named share was still sitting
 * underneath it — the exact mismatch behind FRONT-2029. Returns `null` when
 * there's nothing dormant to report (either live already, or genuinely
 * only-you), so callers can use the return value itself as the "show this"
 * condition.
 */
export function describeDormantAudience(
  app: Pick<AdminApp, 'status' | 'visibility' | 'colleaguesCanOpen' | 'shareScope' | 'shareAllowedEmails'>,
): string | null {
  if (app.status === 'live') return null;
  if (app.visibility === 'public') {
    return 'Anyone with the link will be able to open it once this is live.';
  }
  if (app.colleaguesCanOpen) {
    const who = namedOrWorkspaceWho(app);
    return `Shared with ${who} — they'll be able to open it again once this is live.`;
  }
  return null;
}

export function SharingTab({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-app', slug],
    queryFn: () => api.get<{ app: AdminApp }>(`/api/admin/apps/${slug}`),
  });
  const app = data?.app;
  const draftsOn = useFeatureFlag(APP_DRAFTS_FLAG, false);

  if (isLoading || !app) return <Skeleton className="h-60 w-full" />;

  // FRONT-1880: the audience from the ONE shared derivation (`appRowAudience`,
  // also used by the Apps list), which reads the server's `colleaguesCanOpen`
  // rather than the row's columns — the named list is owner-only in this
  // payload, so a client-side derivation badged the colleague an app was
  // shared with "Only you".
  const audience = appRowAudience(app);
  const dormantAudienceNote = describeDormantAudience(app);
  const appUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/apps/${app.slug}/`
      : `/apps/${app.slug}/`;

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* FRONT-1880: who can open it — the SAME control the coding session's
          top bar mounts, so the two screens cannot describe one app two ways.
          It replaces a three-level Visibility combobox whose middle level
          ("Unlisted") named a distinction only a deleted public gallery ever
          cared about, and whose help text told the user to go to another
          screen to do the one thing they came here for. Audience applies
          immediately, which is why nothing here feeds the ActionBar below —
          that now governs the allowlist alone. */}
      <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Who can open this app</p>
            {/* The hint stands alone: the button beside it carries the value,
                so repeating "Currently Public" here would say the same thing
                twice and still leave the button reading like a verb.
                FRONT-2078: `shared` gets the scope-aware hint, not the
                generic `APP_AUDIENCE_COPY` one — this is the decision
                surface (unlike the Apps list's scan-only badge), so it says
                who, not just "workspace" for a share that's actually one
                named colleague. */}
            {/* FRONT-2091: every sentence this can render is present tense
                ("…can open it"), and since the lifecycle collapse came out of
                the audience rule that is a claim an offline app does not
                honour — a draft is served to its owner alone
                (`resolveServableApp`). The amber note below is already the
                lifecycle-aware version of this exact sentence, scope and all,
                so when it is showing this one would be the same fact stated
                twice with the tenses disagreeing. Show one or the other, never
                both. `describeDormantAudience` returns null for a genuinely
                only-you app, whose hint is true offline too — so that case
                keeps its sentence here. */}
            {!dormantAudienceNote && (
              <p className="text-xs text-muted-foreground">
                {audience === 'shared' ? describeSharedHint(app) : APP_AUDIENCE_COPY[audience].hint}
              </p>
            )}
          </div>
          <AppSharePopover
            slug={slug}
            audience={audience}
            scope={app.shareScope ?? 'workspace'}
            people={app.shareAllowedEmails ?? []}
            pausedPeople={app.pausedShareEmails ?? []}
            listed={app.listedToColleagues !== false}
            url={appUrl}
            audienceLock={app.audienceLock ?? null}
            // FRONT-2091: reachability, which `audience` above stopped
            // carrying. Same source as `describeDormantAudience`'s own check.
            live={app.status === 'live'}
            // A picker, not an action — it replaced a combobox, and the app
            // may already be shared.
            trigger="audience"
            className="shrink-0"
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ['admin-app', slug] });
              qc.invalidateQueries({ queryKey: ['admin-apps'] });
              onSaved();
            }}
          />
        </div>
        {app.audienceLock && (
          // Visible, not a tooltip: the person has to act on this, and the
          // Share button's own copy of it is behind a click.
          <p className="text-xs text-muted-foreground">{app.audienceLock}</p>
        )}
        {/* FRONT-2078: this app is offline right now. The badge above used to
            read "Only you" whatever the stored decision was, so this note
            existed to say the part the badge could not — the FRONT-2029
            incident was exactly an owner reading "Only you" as "I removed my
            colleague's access", when Unpublish never touches it.

            FRONT-2091: the badge now states that decision itself ("Shared",
            "Public"), so this note's job narrowed from CORRECTING the badge
            to supplying the tense — who can open it, and when. It also takes
            over from the present-tense hint above, which is suppressed
            whenever this is showing. */}
        {dormantAudienceNote && (
          <p className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-300">
            <WarningCircleIcon size={14} weight="fill" className="shrink-0 mt-0.5" />
            <span>{dormantAudienceNote}</span>
          </p>
        )}
      </div>

      {showsEditorsSection(app, draftsOn) && app.ownerUserId && <EditorsSection slug={slug} ownerUserId={app.ownerUserId} />}

      {/* FRONT-1775: the legacy `?share=<token>` link (and its withdraw
          row) is gone — the migration dropped `shareToken`/`passwordHash`/
          `sharedAt` outright, so there is nothing left that could still be
          active here. The audience above is the whole sharing decision. */}

      <div className="rounded-md border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground space-y-1">
        <div className="flex justify-between"><span>Views</span><span className="tabular-nums text-foreground">{app.viewCount.toLocaleString()}</span></div>
        <div className="flex justify-between"><span>Last viewed</span><span>{formatDate(app.lastViewedAt)}</span></div>
      </div>
    </div>
  );
}

// ───────────────────────────── Combobox ───────────────────────────────

interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled = false,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <CaretUpDownIcon size={14} className="text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.description ?? ''} ${o.value}`}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    size={14}
                    weight="bold"
                    className={cn('mr-1', o.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">{o.label}</span>
                    {o.description && (
                      <span className="text-xs text-muted-foreground">{o.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ──────────────────────── Email allowlist input ───────────────────────

// FRONT-1880: the old allowlist block lived here — `allowlistEntryWarning`,
// `isValidEntry` and `EmailAllowlistInput`. It moved into the Share control
// (`share-people-input.tsx`) when the audience absorbed the named list, and
// the copies here were left unreferenced. Deleted rather than kept "just in
// case": two implementations of one input is how the token path and the
// access path came to disagree in the first place.

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ──────────────────────────────── API tab ───────────────────────────────

interface AppTokenInfo {
  token: string;
  scopes: string[];
  createdAt: string;
}

interface GraphAllowlistEntity {
  id: string;
  type: string;
  name: string;
}

/**
 * Eddy Apps API tab. Owner can mint/rotate/revoke the app's API token
 * and curate the graph subgraph the app is allowed to read. See
 * FRONT-53 — the SDK reads `window.__EDDY_APP_TOKEN__` baked into the
 * served HTML at request time.
 */
export function ApiTab({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: tokenData, isLoading: tokenLoading } = useQuery({
    queryKey: ['admin-app-token', slug],
    queryFn: () =>
      api.get<{ token: AppTokenInfo | null }>(`/api/admin/apps/${slug}/token`),
  });
  const token = tokenData?.token ?? null;

  // Shares its cache entry with `CapabilitiesSection`'s own query. The token
  // and the activity log answer the owner only; unknown counts as not the owner.
  const { data: settingsData } = useQuery({
    queryKey: ['admin-app-settings', slug],
    queryFn: () => api.get<{ app: { isOwner?: boolean } }>(`/api/admin/apps/${slug}`),
  });
  const isOwner = settingsData?.app?.isOwner === true;

  const mint = useMutation({
    mutationFn: (opts: { rotate: boolean; scopes?: string[] }) =>
      api.post<{ token: AppTokenInfo }>(`/api/admin/apps/${slug}/token`, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('API token ready');
    },
    onError: (e: Error) => toast.error(`Mint failed: ${e.message}`),
  });

  const saveScopes = useMutation({
    mutationFn: (scopes: string[]) =>
      api.patch<{ token: AppTokenInfo }>(
        `/api/admin/apps/${slug}/token/scopes`,
        { scopes },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('Scopes saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/api/admin/apps/${slug}/token`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('API token revoked');
    },
    onError: (e: Error) => toast.error(`Revoke failed: ${e.message}`),
  });

  // Revoking breaks the live app's API access until a new token is created,
  // so it is confirmed first, like the tools and agents off switches.
  async function revokeAfterConfirm() {
    const ok = await confirm({
      title: 'Revoke the API token?',
      description:
        'No scopes are left, so the token is revoked. The live app loses API access until you create a new token.',
      confirmText: 'Revoke',
      destructive: true,
    });
    if (ok) revoke.mutate();
  }

  const tokenPending = mint.isPending || saveScopes.isPending || revoke.isPending;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Token */}
      <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
        <div className="flex flex-col gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">API token</p>
            <p className="text-xs text-muted-foreground">
              Baked into the served HTML so the SDK can read it as
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[length:var(--text-2xs)]">window.__EDDY_APP_TOKEN__</code>.
              The value stays hidden — rotate it if you suspect it leaked.
            </p>
          </div>
          {isOwner && !token && !tokenLoading && (
            <Button
              onClick={() => mint.mutate({ rotate: false })}
              disabled={mint.isPending}
            >
              {mint.isPending ? 'Minting…' : 'Create token'}
            </Button>
          )}
          {token && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => mint.mutate({ rotate: true })}
                disabled={tokenPending}
              >
                {mint.isPending ? 'Rotating…' : 'Rotate'}
              </Button>
            </div>
          )}
        </div>
        {tokenLoading && <Skeleton className="h-9 w-full" />}
        {token && (
          <ScopeEditor
            current={token.scopes}
            isPending={tokenPending}
            isSubmitting={saveScopes.isPending || revoke.isPending}
            onApply={(scopes) => saveScopes.mutate(scopes)}
            onRevoke={() => void revokeAfterConfirm()}
          />
        )}
      </div>

      {/* Graph allowlist */}
      <GraphAllowlistEditor slug={slug} />

      {/* Native-tool allowlist (FRONT-1385) lives ONLY in the Capabilities
          section's "Eddy tools" card (`capabilities-section.tsx`) — mounting
          it here too produced two live editors with duplicate DOM ids
          (`t-${name}`), so a checkbox click in one toggled the other's row
          and either editor's Save discarded the other's in-flight draft. */}

      {/* Pentest helpers: live endpoint smoke-test + recent audit */}
      <SelfTest slug={slug} token={token} />
      {isOwner && <AuditFeed slug={slug} />}

      <div className="rounded-md border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">Using the SDK</p>
        <p>
          The SDK loads automatically into every served app and exposes
          <code className="mx-1 rounded bg-muted px-1 py-0.5">window.eddy</code>
          with two surfaces:
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><code className="rounded bg-muted px-1 py-0.5">eddy.kv.get/set/remove/list</code> — persistent key→JSON storage for this app.</li>
          <li><code className="rounded bg-muted px-1 py-0.5">eddy.graph.list/get</code> — read the entities you allowlist below.</li>
          <li><code className="rounded bg-muted px-1 py-0.5">eddy.data.put/get/list/query/remove</code> — store & query this app's own records.</li>
        </ul>
        <p>
          The served page inherits the theme picked in Branding — Light, Dark, or System
          (follows the visitor's OS). Style with <code className="rounded bg-muted px-1 py-0.5">color-scheme</code>-aware
          CSS or the <code className="rounded bg-muted px-1 py-0.5">prefers-color-scheme</code> media query.
        </p>
      </div>
    </div>
  );
}

interface AuditEntry {
  id: number;
  eventType: string;
  actorKind: string;
  actorId: string | null;
  method: string | null;
  status: string | null;
  ip: string | null;
  payload: Record<string, unknown> | null;
  at: string;
}

const ALL_SCOPES = [
  'app:kv:read',
  'app:kv:write',
  'app:graph:read',
  'app:data:read',
  'app:data:write',
] as const;
type AppScope = (typeof ALL_SCOPES)[number];

const SCOPE_DESCRIPTIONS: Record<AppScope, string> = {
  'app:kv:read': 'Read the app KV bucket',
  'app:kv:write':
    'Write to the app KV bucket. Visitors of the live page can use this — only enable if you actually want anonymous writes.',
  'app:graph:read': 'Read the allowlisted graph entities',
  'app:data:read': "Read this app's data store (its records).",
  'app:data:write':
    "Write to this app's data store. Visitors of the live page can use this — only enable if you want anonymous writes.",
};

export type ScopeEditorSubmit =
  | { kind: 'revoke'; label: string }
  | { kind: 'save'; scopes: string[]; label: string };

/**
 * What the scope editor's submit button does for a selection. The editor
 * only renders once a token exists, and the scopes route rejects an empty
 * set with a 400 ("At least one scope required (or revoke the token
 * instead)"), so an empty selection revokes the token instead — the same
 * routing the capability cards use (FRONT-2817). Pure and exported for
 * direct testing.
 */
export function scopeEditorSubmit(next: string[]): ScopeEditorSubmit {
  const action = resolveApplyScopesAction(true, next);
  if (action.kind === 'save') return { kind: 'save', scopes: action.scopes, label: 'Save scopes' };
  return { kind: 'revoke', label: 'Revoke token' };
}

export function ScopeEditor({
  current,
  isPending,
  isSubmitting = isPending,
  onApply,
  onRevoke,
}: {
  current: string[];
  /** Disables the editor: any token write is in flight (rotate included). */
  isPending: boolean;
  /** This editor's own save/revoke is in flight; drives the button label. */
  isSubmitting?: boolean;
  onApply: (scopes: string[]) => void;
  onRevoke: () => void;
}) {
  const [draft, setDraft] = useState<string[] | null>(null);
  const value = draft ?? current;
  const dirty = draft !== null && !sameSet(draft, current);
  const submit = scopeEditorSubmit(value);
  const pendingLabel = submit.kind === 'revoke' ? 'Revoking…' : 'Saving…';

  const toggle = (s: string) =>
    setDraft((prev) => {
      const base = prev ?? current;
      return base.includes(s) ? base.filter((x) => x !== s) : [...base, s];
    });

  return (
    <div className="space-y-2">
      <p className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground font-semibold">
        Scopes
      </p>
      <div className="space-y-1.5">
        {ALL_SCOPES.map((s) => {
          const id = `scope-${s}`;
          const checked = value.includes(s);
          return (
            <label
              key={s}
              htmlFor={id}
              className="flex items-start gap-2 text-xs cursor-pointer"
            >
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={() => toggle(s)}
                disabled={isPending}
              />
              <div>
                <code className="text-[length:var(--text-2xs)]">{s}</code>
                <p className="text-[length:var(--text-2xs)] text-muted-foreground">
                  {SCOPE_DESCRIPTIONS[s]}
                </p>
              </div>
            </label>
          );
        })}
      </div>
      {dirty && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[length:var(--text-2xs)] text-muted-foreground">
            {submit.kind === 'revoke'
              ? 'No scopes left — the token will be revoked.'
              : 'Token stays the same — only permissions change.'}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDraft(null)}
              disabled={isPending}
            >
              Reset
            </Button>
            <Button
              size="sm"
              variant={submit.kind === 'revoke' ? 'destructive' : 'default'}
              onClick={() => (submit.kind === 'revoke' ? onRevoke() : onApply(submit.scopes))}
              disabled={isPending}
            >
              {isSubmitting ? pendingLabel : submit.label}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

interface SelfTestResult {
  endpoint: string;
  method: string;
  status: number;
  ok: boolean;
  body: string;
  /** Friendly one-liner derived after the call. */
  summary: string;
  /** PASS = behaviour matches scope grants. FAIL = unexpected outcome. */
  passed: boolean;
}

function SelfTest({ slug, token }: { slug: string; token: AppTokenInfo | null }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<SelfTestResult[] | null>(null);

  async function hit(
    method: string,
    path: string,
    requiredScope: string,
    body?: unknown,
  ): Promise<SelfTestResult> {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token.token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let res: Response;
    try {
      res = await fetch(`/api/apps/${slug}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'omit',
      });
    } catch (e) {
      return {
        endpoint: path,
        method,
        status: 0,
        ok: false,
        body: `Network error: ${(e as Error).message}`,
        summary: 'Network error — server unreachable.',
        passed: false,
      };
    }
    const text = await res.text();
    const granted = (token?.scopes ?? []).includes(requiredScope);
    const expected = granted ? 200 : 403;
    const actuallyAllowed = res.ok;
    let summary: string;
    let passed: boolean;
    if (granted && actuallyAllowed) {
      summary = `Allowed — token has ${requiredScope}.`;
      passed = true;
    } else if (!granted && res.status === 403) {
      summary = `Correctly blocked — token lacks ${requiredScope}.`;
      passed = true;
    } else if (!granted && actuallyAllowed) {
      summary = `Unexpected success — token lacks ${requiredScope}. Scope gate may be broken.`;
      passed = false;
    } else if (granted && !actuallyAllowed) {
      summary = `Unexpected ${res.status} — token has ${requiredScope}; investigate.`;
      passed = false;
    } else {
      summary = `Unexpected status ${res.status} (expected ${expected}).`;
      passed = false;
    }
    return {
      endpoint: path,
      method,
      status: res.status,
      ok: res.ok,
      body: text.slice(0, 200),
      summary,
      passed,
    };
  }

  async function run() {
    setRunning(true);
    setResults(null);
    const out: SelfTestResult[] = [];
    out.push(await hit('GET', '/kv', 'app:kv:read'));
    out.push(await hit('GET', '/graph', 'app:graph:read'));
    out.push(
      await hit('PUT', '/kv/self_test', 'app:kv:write', {
        value: { at: new Date().toISOString() },
      }),
    );
    setResults(out);
    setRunning(false);
  }

  return (
    <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Self-test</p>
          <p className="text-xs text-muted-foreground">
            Hits each endpoint with the live token and reports the status. Use this to verify scope boundaries.
          </p>
        </div>
        <Button size="sm" onClick={run} disabled={running || !token}>
          {running ? 'Running…' : 'Run self-test'}
        </Button>
      </div>
      {!token && (
        <p className="text-xs text-muted-foreground italic">Mint a token to enable the self-test.</p>
      )}
      {results && (
        <div className="space-y-1.5">
          {results.map((r, i) => (
            <SelfTestRow key={i} slug={slug} result={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function SelfTestRow({ slug, result }: { slug: string; result: SelfTestResult }) {
  const [open, setOpen] = useState(false);
  const tone = result.passed
    ? 'border-success/30 bg-success/10 text-success'
    : 'border-destructive/30 bg-destructive/10 text-destructive';
  return (
    <div className={cn('rounded border px-2.5 py-2 text-xs', tone)}>
      <div className="flex items-center gap-2">
        <span className="font-semibold tabular-nums">
          {result.passed ? '✓' : '✗'}
        </span>
        <span className="font-mono text-[length:var(--text-2xs)] font-semibold">
          {result.method}
        </span>
        <span className="font-mono text-[length:var(--text-2xs)] text-muted-foreground truncate">
          /api/apps/{slug}
          {result.endpoint}
        </span>
        <span className="ml-auto tabular-nums text-[length:var(--text-2xs)] opacity-80">
          {result.status}
        </span>
      </div>
      <p className="mt-0.5 text-[length:var(--text-2xs)] leading-snug">{result.summary}</p>
      {result.body && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-[length:var(--text-2xs)] uppercase tracking-wider opacity-70 hover:opacity-100 cursor-pointer"
        >
          {open ? 'Hide raw response' : 'Show raw response'}
        </button>
      )}
      {open && result.body && (
        <pre className="mt-1 whitespace-pre-wrap break-all text-[length:var(--text-2xs)] opacity-80">
          {result.body}
        </pre>
      )}
    </div>
  );
}

/** Why the audit feed is empty when its read failed: a 404 is the server saying it is not this viewer's to read (FRONT-2942). */
export function auditFeedFailure(error: unknown): string {
  return error instanceof ApiError && error.status === 404
    ? "The audit feed is only for this app's owner and editors."
    : "Couldn't load the audit feed. Try Refresh.";
}

function AuditFeed({ slug }: { slug: string }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-app-audit', slug],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(`/api/admin/apps/${slug}/audit?limit=50`),
  });
  const entries = data?.entries ?? [];
  return (
    <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Audit feed</p>
          <p className="text-xs text-muted-foreground">
            Last 50 security-relevant events for this app — API calls, token lifecycle, share + domain changes, graph allowlist edits.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
      {isLoading && <Skeleton className="h-24 w-full" />}
      {isError && <p className="text-xs text-muted-foreground italic">{auditFeedFailure(error)}</p>}
      {!isLoading && !isError && entries.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No events yet.</p>
      )}
      {entries.length > 0 && (
        <div className="max-h-72 overflow-auto rounded border border-border/60 divide-y divide-border/60 text-[length:var(--text-2xs)]">
          {entries.map((e) => (
            <div key={e.id} className="px-3 py-1.5 flex items-center gap-2">
              <span className="font-mono text-muted-foreground tabular-nums shrink-0">
                {new Date(e.at).toLocaleTimeString()}
              </span>
              <span className="font-medium shrink-0">{e.eventType}</span>
              <span className="text-muted-foreground shrink-0">{e.actorKind}</span>
              {e.method && (
                <span className="text-muted-foreground">{e.method}</span>
              )}
              {e.status && (
                <span className={`tabular-nums ${e.status.startsWith('2') ? 'text-success' : 'text-amber-500'}`}>
                  {e.status}
                </span>
              )}
              {e.ip && <span className="text-muted-foreground ml-auto">{e.ip}</span>}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">Shows the last 15 days of page calls.</p>
    </div>
  );
}

function GraphAllowlistEditor({ slug }: { slug: string }) {
  const qc = useQueryClient();

  const { data: allowData, isLoading: allowLoading } = useQuery({
    queryKey: ['admin-app-graph-access', slug],
    queryFn: () =>
      api.get<{ entityIds: string[] }>(`/api/admin/apps/${slug}/graph-access`),
  });
  const allowed = allowData?.entityIds ?? [];

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const PAGE_SIZE = 50;

  const {
    data: pages,
    isFetching: entitiesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['graph-entities-paged', debouncedSearch] as const,
    queryFn: ({ pageParam }) => {
      const url = new URL('/api/graph/entities', window.location.origin);
      url.searchParams.set('limit', String(PAGE_SIZE));
      if (debouncedSearch) url.searchParams.set('q', debouncedSearch);
      if (pageParam) url.searchParams.set('cursor', pageParam);
      return api.get<{
        items: GraphAllowlistEntity[];
        hasMore: boolean;
        nextCursor: string | null;
      }>(url.pathname + url.search);
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const allEntities = useMemo(
    () => (pages?.pages ?? []).flatMap((p) => p.items),
    [pages],
  );

  const save = useMutation({
    mutationFn: (entityIds: string[]) =>
      api.put(`/api/admin/apps/${slug}/graph-access`, { entityIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-graph-access', slug] });
      toast.success('Allowlist saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const [draft, setDraft] = useState<string[] | null>(null);
  const current = draft ?? allowed;
  useEffect(() => {
    if (allowData) setDraft(null);
  }, [allowData]);

  function toggle(id: string) {
    setDraft((prev) => {
      const base = prev ?? allowed;
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
  }

  const dirty = draft !== null;

  return (
    <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium">Graph allowlist</p>
        <p className="text-xs text-muted-foreground">
          Pick which entities this app may read via <code className="rounded bg-muted px-1 py-0.5">eddy.graph.*</code>. Empty = no graph access.
        </p>
      </div>
      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search entities by name…"
      />
      {allowLoading && <Skeleton className="h-32 w-full" />}
      {!allowLoading && (
        <div className="max-h-72 overflow-auto rounded border border-border/60 divide-y divide-border/60">
          {allEntities.length === 0 && !entitiesLoading && (
            <p className="text-xs text-muted-foreground px-3 py-2">
              {debouncedSearch ? 'No matches.' : 'No entities in your graph yet.'}
            </p>
          )}
          {allEntities.map((e) => {
            const id = `g-${e.id}`;
            const checked = current.includes(e.id);
            return (
              <label
                key={e.id}
                htmlFor={id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer"
              >
                <Checkbox id={id} checked={checked} onCheckedChange={() => toggle(e.id)} />
                <span className="text-sm">{e.name}</span>
                <span className="ml-auto text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
                  {e.type}
                </span>
              </label>
            );
          })}
          {hasNextPage && (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="w-full px-3 py-2 text-xs text-muted-foreground hover:bg-muted/40 cursor-pointer disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
      {dirty && (
        <div className="flex justify-end">
          <Button onClick={() => save.mutate(current)} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save allowlist'}
          </Button>
        </div>
      )}
    </div>
  );
}
